import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar, 
  Avatar, 
  Divider, 
  Paper, 
  CircularProgress,
  Chip
} from '@mui/material';
import { getUserReferrals } from '../services/users';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonIcon from '@mui/icons-material/Person';

const UserReferralsList = ({ userId }) => {
  const [referralData, setReferralData] = useState({
    referrals: [],
    hasReferrals: false,
    totalCount: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Load referrals when component mounts or userId changes
  useEffect(() => {
    const fetchReferrals = async () => {
      if (!userId) return;
      
      setLoading(true);
      setError('');
      
      try {
        const data = await getUserReferrals(userId);
        
        // Debug: log the first referral to see what fields are available
        if (data.referrals.length > 0) {
          console.log('First referral data:', data.referrals[0]);
        }
        
        setReferralData(data);
      } catch (err) {
        console.error('Error fetching referrals:', err);
        setError('Failed to load referrals. ' + (err.message || ''));
      } finally {
        setLoading(false);
      }
    };
    
    fetchReferrals();
  }, [userId]);
  
  // Format date in a user-friendly way
  const formatDate = (date) => {
    if (!date) return 'Unknown';
    
    // Convert various date formats to a JavaScript Date object
    let dateObj;
    
    try {
      // If it's a Firestore timestamp with seconds and nanoseconds
      if (date && typeof date === 'object' && date.seconds !== undefined) {
        dateObj = new Date(date.seconds * 1000);
      }
      // If it's a Firestore server timestamp with _seconds
      else if (date && typeof date === 'object' && date._seconds !== undefined) {
        dateObj = new Date(date._seconds * 1000);
      }
      // If it's a Date object
      else if (date instanceof Date) {
        dateObj = date;
      }
      // If it's a timestamp number
      else if (typeof date === 'number') {
        dateObj = new Date(date);
      }
      // If it's an ISO string or other date string
      else if (typeof date === 'string') {
        // Check if it's a numeric timestamp string
        if (!isNaN(parseInt(date)) && date.length >= 10 && date.length <= 13) {
          const timestamp = parseInt(date);
          // If milliseconds (13 digits)
          if (date.length >= 13) {
            dateObj = new Date(timestamp);
          } 
          // If seconds (10 digits)
          else {
            dateObj = new Date(timestamp * 1000);
          }
        } else {
          // Regular date string
          dateObj = new Date(date);
        }
      } else {
        return 'Invalid date format';
      }
      
      // Check if the date is valid
      if (isNaN(dateObj.getTime())) {
        console.warn("Invalid date object created from:", date);
        return 'Invalid date';
      }
      
      const now = new Date();
      
      // If date is today, show only time
      if (dateObj.toDateString() === now.toDateString()) {
        return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
      
      // If date is this year, show month and day
      if (dateObj.getFullYear() === now.getFullYear()) {
        return dateObj.toLocaleDateString([], { month: 'short', day: 'numeric' });
      }
      
      // Otherwise show full date
      return dateObj.toLocaleDateString();
    } catch (e) {
      console.error("Error formatting date:", e, "Input:", date);
      return 'Invalid date';
    }
  };

  // Handle click on user
  const handleUserClick = (userId) => {
    navigate(`/dashboard/user/${userId}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, color: 'error.main' }}>
        <Typography variant="subtitle1">Error loading referrals</Typography>
        <Typography variant="body2">{error}</Typography>
      </Box>
    );
  }

  if (!referralData.hasReferrals || referralData.referrals.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <PersonAddIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
        <Typography variant="subtitle1" color="text.secondary">
          This user has no referrals
        </Typography>
      </Box>
    );
  }

  return (
    <Paper variant="outlined" sx={{ mt: 2 }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Referrals ({referralData.totalCount})
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
            <Box component="span" sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              border: '1px solid', 
              borderColor: 'divider', 
              borderRadius: 1, 
              px: 1, 
              py: 0.5 
            }}>
              Sorted by: Newest first
            </Box>
          </Typography>
        </Box>
      </Box>
      
      <List sx={{ maxHeight: '600px', overflow: 'auto' }}>
        {referralData.referrals.map((referral, index) => (
          <React.Fragment key={referral.referredUserId}>
            <ListItem 
              alignItems="flex-start" 
              button
              onClick={() => handleUserClick(referral.referredUserId)}
              sx={{ 
                '&:hover': { 
                  bgcolor: 'action.hover' 
                }
              }}
            >
              <ListItemAvatar>
                <Avatar src={referral.userProfile?.avatar || ''}>
                  <PersonIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography component="span" variant="subtitle1" fontWeight="medium">
                      {referral.userProfile?.name || 'Unknown User'} 
                      {referral.userProfile?.userName ? <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        @{referral.userProfile.userName}
                      </Typography> : null}
                    </Typography>
                    <Chip
                      size="small"
                      label="Referred"
                      color="success"
                      variant="outlined"
                      icon={<PersonAddIcon />}
                    />
                  </Box>
                }
                secondary={
                  <React.Fragment>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography component="div" variant="caption" color="text.secondary">
                        Referred on: {referral.created ? formatDate(referral.created) : 'Unknown date'}
                      </Typography>
                      <Typography component="div" variant="caption" color="text.secondary">
                        User ID: {referral.referredUserId}
                      </Typography>
                    </Box>
                  </React.Fragment>
                }
              />
            </ListItem>
            {index < referralData.referrals.length - 1 && <Divider component="li" />}
          </React.Fragment>
        ))}
      </List>
    </Paper>
  );
};

export default UserReferralsList;